import { useState, useEffect, Suspense } from 'react';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import css from '../../styles/modules/faq/Home.module.scss';
import Image from 'next/image';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HomeNewsSkeletonItem from '../../components/home/HomeNewsSkeletonItem';
import { useIsMount } from '../../hooks/useIsMount';
import { useNewsProvider } from '../../context/news';

const generatePagination = (current, last) => {
  const delta = 1;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];

  let l;

  for (let i = 1; i <= last; i += 1) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push(null);
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const News = ({ dataNews, current_page, last_page }) => {
  const router = useRouter();
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const isMount = useIsMount();
  const [newsState, setNewsState] = useNewsProvider();

  /** HOOKS */
  useEffect(() => {
    if (isMount) {
      setPage(current_page);
      setPages(last_page);
      setData(dataNews);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        //await sleep(300);

        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/faq/top', {
          length: 9,
          page: page
        }, { signal });

        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);

        setPage(response.data.data.current_page);
        setData(response.data.data.data);
        setIsLoadingData(false);
        setIsError(false);

      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoadingData(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [newsState.pagination]);

  const DateFormatter = new Intl.DateTimeFormat('ru-Ru', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  /** FUNCTIONS */
  const handlePagingClick = (value, modificator = 0) => {
    if (value + modificator === page || value + modificator <= 0 || value + modificator > pages)
      return;

    setPage(value + modificator);
    setNewsState({ type: 'SET_PAGINATION', payload: { page: value + modificator } });
    router.replace(`/news?page=` + (value + modificator));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Fragment>
      <NextSeo
        title={'Новости недвижимости - страница ' + page}
        description={'Новости недвижимости в России: последние новости о недвижимости, ипотеке, спрос, аналитика, изменения в налогах, ключевая ставка, новости росреестра, новости ипотека, блок о недвижимости. Страница:' + page}
        openGraph={{
          url: `https://${process.env.NEXT_PUBLIC_SITEDOMAIN}/faq/`,
          type: 'website',
          title: 'Новости недвижимости - страница' + page,
          description: 'Новости недвижимости в России: последние новости о недвижимости, ипотеке, спрос, аналитика, изменения в налогах, ключевая ставка, новости росреестра, новости ипотека, блок о недвижимости. Страница : ' + page,
          images: [
            {
              url: '/images/og-preview.png'
            }
          ],
          site_name: process.env.NEXT_PUBLIC_SITENAME
        }}
      />
      <Header />
      <main className="core">
        <section className="block">
          <div className="fixed">
            <div className={css.newsList}>
              {data && data.length == 0 && (
                <div>Новостей нет</div>
              )}
              {data && data.map((value, index) => (
                <Suspense fallback={<HomeNewsSkeletonItem />} key={index}>
                  <a className={css.newsListItem} href={`/${value.slugCategory}/${value.slug}`}>
                    {value.image && (<Image className={css.newsListItemImage} src={value.image} alt={value.slug} width={320} height={240} />)}
                    <div className={css.newsListItemContent}>
                      <span>{value.title}</span>
                      <div className={css.newsItemShortDescription}>
                        {value.short_body}
                      </div>
                      <div className={css.newsListItemFooter}>
                        <div className={css.newsListItemCategories}>
                          {value.category.length ? value.category.join(' | ') : ''}
                        </div>
                        <div className={css.newsListItemDate}>
                          {DateFormatter.format(new Date(value.created_at))}
                        </div>
                      </div>
                    </div>
                  </a>
                </Suspense>
              )
              )}
            </div>

            {pages > 1 && (
              <div className={css.catalogFooter}>
                <button
                  className={css.catalogFooterLeft}
                  type="button"
                  aria-label="Предыдущая страница"
                  onClick={() => handlePagingClick(page, -1)}
                  style={{
                    cursor: page <= 1 ? 'not-allowed' : null,
                    opacity: page <= 1 ? 0.1 : null
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                {generatePagination(page, pages).map((value, index) => {
                  if (!value) return <span key={index}>...</span>;

                  return (
                    <button
                      className={[css.catalogFooterButton, value === page ? css.active : ''].join(' ')}
                      type="button"
                      key={index}
                      onClick={() => handlePagingClick(value)}
                      style={{ cursor: value === page ? 'default' : null }}
                    >
                      {value}
                    </button>
                  );
                })}
                <button
                  className={css.catalogFooterRight}
                  type="button"
                  aria-label="Следующая страница"
                  onClick={() => handlePagingClick(page, 1)}
                  style={{
                    cursor: page >= pages ? 'not-allowed' : null,
                    opacity: page >= pages ? 0.1 : null
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </Fragment>
  );
};

export async function getServerSideProps(context) {
  let page = 0;
  if (context.query.page && context.query.page > 0)
    page = context.query.page;

  try {
    const response = await axios.get(process.env.NEXT_PUBLIC_API + '/faq/top?length=9&page=' + page);

    if (!response.data || response.data.status !== 'ok') {
      return {
        redirect: {
          destination: '/?not_found',
          permanent: true
        }
      };
    }

    return {
      props: {
        dataNews: response.data.data.data,
        current_page: response.data.data.current_page,
        last_page: response.data.data.last_page
      }
    };

  } catch (error) {
    return {
      notFound: true
    };
  }
};

export default News;
